import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Paper, TextField, Typography } from "@mui/material";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import moment from "moment";
import { DeliveryBoardService } from "../../services/DeliveryBoardService";
import { IDeliverySettlementGroup } from "../../common/interfaces";
import { money } from "./deliveryFormat";

const MySwal = withReactContent(Swal);

interface Props {
  warehouseId: number;
  cashBoxId?: number;
}

// Liquidación del efectivo que cobraron los repartidores: la cajera cuenta lo que le entregan,
// lo captura y confirma. Cada venta queda cobrada en la caja abierta; la diferencia queda registrada.
export const DeliverySettlement: React.FC<Props> = ({ warehouseId, cashBoxId }) => {
  const [groups, setGroups] = useState<IDeliverySettlementGroup[]>([]);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [counted, setCounted] = useState<Record<number, string>>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<number | null>(null);

  const load = useCallback(async () => {
    const res = await DeliveryBoardService.getSettlement(warehouseId);
    if (res.success && res.object) {
      setGroups(res.object);
      setError("");
      const all: Record<number, boolean> = {};
      res.object.forEach((g) => g.deliveries.forEach((d) => (all[d.id] = true)));
      setSelected(all);
    } else {
      setError(res.status === 403 ? "Tu usuario no tiene permiso para liquidar repartidores." : res.message || "No se pudo cargar la liquidación.");
    }
  }, [warehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  const settle = async (group: IDeliverySettlementGroup) => {
    if (!group.driver || !cashBoxId) return;
    const ids = group.deliveries.filter((d) => selected[d.id]).map((d) => d.id);
    const expected = group.deliveries.filter((d) => selected[d.id]).reduce((s, d) => s + d.amount, 0);
    const amount = parseFloat((counted[group.driver.id] || "").replace(",", "."));
    if (!ids.length || isNaN(amount)) return;

    const diff = Math.round((amount - expected) * 100) / 100;
    const confirm = await MySwal.fire({
      title: `Liquidar a ${group.driver.name}`,
      html: `Esperado: <b>${money(expected)}</b><br/>Contado: <b>${money(amount)}</b>` +
        (diff !== 0 ? `<br/><span style="color:${diff < 0 ? "#ff8a80" : "#8fdc8f"}">${diff < 0 ? "Faltante" : "Sobrante"}: ${money(Math.abs(diff))}</span>` : ""),
      icon: diff === 0 ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setSaving(group.driver.id);
    const res = await DeliveryBoardService.settle({ driver_id: group.driver.id, delivery_ids: ids, counted: amount, cash_box_id: cashBoxId });
    setSaving(null);
    if (res.success) {
      MySwal.fire({ icon: "success", title: res.message || "Liquidación registrada", timer: 2500, showConfirmButton: false });
      setCounted((c) => ({ ...c, [group.driver!.id]: "" }));
      load();
    } else {
      MySwal.fire({ icon: "error", title: "Error", text: res.message || "No se pudo liquidar." });
    }
  };

  if (!cashBoxId) return <Alert severity="warning">Abre la caja para liquidar el efectivo de los repartidores.</Alert>;

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      {!!error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {!error && groups.length === 0 && <Typography sx={{ color: "#aaa" }}>No hay efectivo pendiente de liquidar.</Typography>}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 1.5 }}>
        {groups.map((g) => {
          const driverId = g.driver?.id ?? 0;
          const expected = g.deliveries.filter((d) => selected[d.id]).reduce((s, d) => s + d.amount, 0);
          return (
            <Paper key={driverId} sx={{ bgcolor: "#222", p: 1.5 }}>
              <Typography sx={{ fontWeight: 800, color: "#F5E03B" }}>{g.driver?.name || "Sin repartidor"}</Typography>
              <Typography sx={{ fontSize: 12, color: "#aaa", mb: 1 }}>Por liquidar: {money(g.total)}</Typography>
              {g.deliveries.map((d) => (
                <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #333" }}>
                  <Checkbox size="small" checked={!!selected[d.id]} onChange={(e) => setSelected((s) => ({ ...s, [d.id]: e.target.checked }))} />
                  <Typography sx={{ fontSize: 13, flex: 1 }}>
                    #{d.venta_id} {d.customer ? `· ${d.customer}` : ""}
                    <br />
                    <span style={{ color: "#888", fontSize: 11 }}>{d.delivered_at ? moment(d.delivered_at).format("DD/MM HH:mm") : ""}</span>
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{money(d.amount)}</Typography>
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                <TextField
                  size="small"
                  label="Efectivo contado"
                  value={counted[driverId] || ""}
                  onChange={(e) => setCounted((c) => ({ ...c, [driverId]: e.target.value }))}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  color="success"
                  disabled={!expected || !counted[driverId] || saving === driverId}
                  onClick={() => settle(g)}
                >
                  Liquidar {money(expected)}
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};
